// src/pages/subscription/EntitlementListPage.tsx
// Entitlements are tenant-scoped, read-only projections of plans/add-ons
// (ListEntitlements takes a tenant id; there are no create/update endpoints),
// so the page asks for a tenant first and the only "form" here is the
// EntitlementCheckDialog probe.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionEntitlement, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { EntitlementCheckDialog } from '@/features/subscription/EntitlementCheckDialog';
import { formatDate, humanizeEnum } from '@/features/subscription/format';

export const EntitlementListPage = observer(() => {
  const store = useSubscriptionEntitlement();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get('tenant') || '';
  const [tenantInput, setTenantInput] = useState(tenantId);
  const [showCheck, setShowCheck] = useState(false);

  useEffect(() => {
    setTenantInput(tenantId);
    if (!tenantId) return;
    store.ListEntitlements(tenantId, {
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], view: 0,
    });
  }, [searchParams]);

  const handleLoad = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(tenantInput.trim() ? { tenant: tenantInput.trim() } : {});
  };

  const renderUsageBar = (item: any) => {
    const type = item.type || '';
    const isNumeric = type === 'ENTITLEMENT_TYPE_NUMERIC' || type === 'ENTITLEMENT_TYPE_METERED';
    if (!isNumeric) return '--';

    const limit = item.limit_value ?? 0;
    const usage = item.current_usage ?? 0;
    const percentage = limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;
    const barColor = percentage >= 90 ? 'bg-destructive' : percentage >= 70 ? 'bg-warning' : 'bg-primary';

    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${percentage}%` }} />
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {usage} / {limit > 0 ? limit : '--'}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Entitlements</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Entitlements</h1>
        </div>
        <Button variant="secondary" onClick={() => setShowCheck(true)}>{t('subscription.entitlement.check')}</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleLoad} className="flex gap-4">
            <Input
              placeholder={t('subscription.entitlement.field.tenant')}
              value={tenantInput}
              onChange={(e) => setTenantInput(e.target.value)}
              fullWidth={false}
              className="flex-1 font-mono"
            />
            <Button type="submit" variant="secondary">{t('subscription.entitlement.list.load')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Feature Key</TableHead>
              <TableHead>Feature Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Limit / Usage</TableHead>
              <TableHead>Source Plan</TableHead>
              <TableHead>Expires At</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!tenantId ? (
              <TableEmpty colSpan={8} message={t('subscription.entitlement.list.prompt')} />
            ) : store.isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={8} message="No entitlements found" />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell className="font-mono text-sm">{String(item.feature_key ?? '--')}</TableCell>
                    <TableCell>{String(item.feature_name ?? '--')}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" size="sm">{humanizeEnum(item.type, 'ENTITLEMENT_TYPE_')}</Badge>
                    </TableCell>
                    <TableCell>
                      {item.is_enabled !== false
                        ? <Badge variant="success" size="sm">Enabled</Badge>
                        : <Badge variant="secondary" size="sm">Disabled</Badge>}
                    </TableCell>
                    <TableCell>{renderUsageBar(item)}</TableCell>
                    <TableCell className="font-mono text-sm">{String(item.source_plan_slug || '--')}</TableCell>
                    <TableCell>{formatDate(item.expires_at)}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/subscriptions/entitlements/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <EntitlementCheckDialog
        open={showCheck}
        onClose={() => setShowCheck(false)}
        initial={{ tenantId }}
      />
    </div>
  );
});

export default EntitlementListPage;
